import React, { useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { toLocalISODate } from "../lib/dates";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { usePagedSearch, SearchBox, Pager } from "./Pager";

const matchPayment = (p, t) => `${p.member_name || ""} ${p.member_no || ""} ${p.receipt_no || ""} ${p.method || ""} ${p.account_name || ""}`.toLowerCase().includes(t);

function VoidPayment({ p, onVoided }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/admin/payments/${p.id}`, { data: { reason } }); toast.success(`Deleted $${Number(data.amount).toFixed(2)} payment for ${data.member || p.member_name}`); setOpen(false); setReason(""); onVoided(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Delete erroneous payment" onClick={() => setOpen(true)} data-testid={`ledger-void-${p.id}`}><Trash2 size={14} /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="void-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">Delete payment</DialogTitle></DialogHeader>
          <div className="rounded-xl border border-gold/30 bg-parchment-card p-3 text-sm">
            <p className="font-semibold">{p.member_name} · ${Number(p.amount).toFixed(2)} · {p.method}</p>
            <p className="text-xs text-muted-foreground">{toLocalISODate(p.created_at)} · {p.account_name} · {p.receipt_no}</p>
          </div>
          <p className="text-xs text-muted-foreground">The member's balance will increase by this amount. A copy with your reason is kept in the audit log and voided-payments archive.</p>
          <div><Label>Reason (required)</Label><Textarea rows={3} placeholder="e.g. Entered twice by mistake — duplicate of RCP-…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="void-reason" /></div>
          <Button onClick={run} disabled={busy || reason.trim().length < 5} variant="destructive" className="w-full" data-testid="void-confirm"><Trash2 size={15} className="mr-2" /> Delete payment</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

const qs = (f) => new URLSearchParams({ account: f.account === "all" ? "" : f.account, date_from: f.from, date_to: f.to }).toString();

function TagSelect({ p, accounts, onTagged }) {
  const tag = async (v) => {
    try { await api.put(`/admin/payments/${p.id}/account`, { account_id: v }); toast.success("Payment tagged"); onTagged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Select value={p.account_id || ""} onValueChange={tag}>
      <SelectTrigger className="h-7 w-36 text-xs" data-testid={`ledger-tag-${p.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
      <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function LedgerFilters({ f, setF, accounts, pg, data }) {
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3">
      <div><Label>Account</Label>
        <Select value={f.account} onValueChange={(v) => setF({ ...f, account: v })}>
          <SelectTrigger className="w-44" data-testid="ledger-account"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select></div>
      <div><Label>From</Label><Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid="ledger-from" /></div>
      <div><Label>To</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="ledger-to" /></div>
      <div><Label>Search</Label><SearchBox value={pg.q} onChange={pg.setQuery} placeholder="Member name, ID, receipt, method…" testId="ledger-search" /></div>
      <Button variant="outline" className="gap-2" data-testid="ledger-export" onClick={() => downloadPdf(`/admin/payments/ledger.csv?${qs(f)}`, "payments-ledger.csv")}>
        <Download size={15} /> Export CSV
      </Button>
      <span className="ml-auto font-mono-accent text-sm" data-testid="ledger-total">{data?.count ?? "—"} payments · ${data?.total?.toFixed(2) ?? "—"}</span>
    </div>
  );
}

function BulkTagBar({ sel, setSel, accounts, onDone }) {
  const [bulkAcc, setBulkAcc] = useState("");
  const bulkTag = async () => {
    try { const { data: r } = await api.post("/admin/payments/bulk-tag", { payment_ids: sel, account_id: bulkAcc }); toast.success(`${r.updated} payment(s) tagged ${r.account_name}`); setSel([]); onDone(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  if (sel.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-gold/40 bg-gold-light/60 p-3" data-testid="bulk-tag-bar">
      <span className="text-sm font-semibold text-crimson">{sel.length} selected</span>
      <Select value={bulkAcc} onValueChange={setBulkAcc}>
        <SelectTrigger className="h-8 w-44" data-testid="bulk-tag-account"><SelectValue placeholder="Choose account" /></SelectTrigger>
        <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" onClick={bulkTag} disabled={!bulkAcc} className="bg-crimson" data-testid="bulk-tag-apply">Tag selected</Button>
      <Button size="sm" variant="ghost" onClick={() => setSel([])} data-testid="bulk-tag-clear">Clear</Button>
    </div>
  );
}

const METHOD_LABEL = { cash: "Cash", cashapp: "Cash App", zelle: "Zelle", check: "Check", stripe: "Stripe (online)", other: "Other" };

function MethodSelect({ p, onChanged }) {
  const { can } = useAuth();
  const locked = p.method === "stripe" || !can("finance");
  if (locked) return <span className="rounded bg-parchment px-2 py-0.5 text-xs" data-testid={`ledger-method-${p.id}`}>{METHOD_LABEL[p.method] || p.method}</span>;
  const change = async (v) => {
    try { await api.put(`/admin/payments/${p.id}/method`, { method: v }); toast.success(`Method set to ${METHOD_LABEL[v]}`); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Select value={p.method || ""} onValueChange={change}>
      <SelectTrigger className="h-7 w-28 text-xs" data-testid={`ledger-method-${p.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(METHOD_LABEL).filter(([k]) => k !== "stripe").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function LedgerRow({ p, selected, onSelect, accounts, canVoid, reload }) {
  return (
    <tr className="border-b border-gold/15" data-testid={`ledger-row-${p.id}`}>
      <td className="p-3"><Checkbox checked={selected} onCheckedChange={onSelect} data-testid={`ledger-select-${p.id}`} /></td>
      <td className="p-3">{toLocalISODate(p.created_at)}</td>
      <td className="p-3">{p.member_name} <span className="font-mono-accent text-xs text-muted-foreground">{p.member_no}</span></td>
      <td className="p-3"><TagSelect p={p} accounts={accounts} onTagged={reload} /></td>
      <td className="p-3"><MethodSelect p={p} onChanged={reload} /></td>
      <td className="p-3 text-xs text-muted-foreground">{p.description}</td>
      <td className="p-3 text-right font-mono-accent font-semibold">${Number(p.amount).toFixed(2)}</td>
      {canVoid && <td className="p-1"><VoidPayment p={p} onVoided={reload} /></td>}
    </tr>
  );
}

export function PaymentsLedger({ canVoid = false }) {
  const methodParam = new URLSearchParams(window.location.search).get("method") || "";
  const [f, setF] = useState({ account: "all", from: "", to: "" });
  const [sel, setSel] = useState([]);
  const { data: accountsData } = useApi("/admin/payment-accounts");
  const accounts = accountsData || [];
  const { data, reload } = useApi(`/admin/payments/ledger?${qs(f)}`, [f.account, f.from, f.to]);
  const [method, setMethod] = useState(["stripe", "offline"].includes(methodParam) ? methodParam : "all");
  const byMethod = useMemo(() => (data?.payments || []).filter((p) => method === "all" || (method === "stripe" ? p.method === "stripe" : p.method !== "stripe")), [data, method]);
  const pg = usePagedSearch(byMethod, "ledger-page-size", matchPayment);
  const rows = pg.shown;
  const allSel = rows.length > 0 && rows.every((p) => sel.includes(p.id));
  const toggleAll = (v) => setSel(v ? [...new Set([...sel, ...rows.map((p) => p.id)])] : sel.filter((id) => !rows.some((p) => p.id === id)));
  const toggleOne = (id) => (v) => setSel((s) => (v ? [...s, id] : s.filter((x) => x !== id)));
  const filteredTotal = pg.q ? ` · $${pg.filtered.reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2)}` : "";
  return (
    <div id="payments-ledger" className="mt-10 scroll-mt-24" data-testid="payments-ledger">
      <h3 className="font-serif-display text-xl font-bold text-crimson">Payments ledger</h3>
      <p className="text-sm text-muted-foreground">Filter by account and date range, re-tag historic payments, correct the payment method, or export to CSV.</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="ledger-method-filter">
        {[["all", "All methods"], ["offline", "Offline (cash, Zelle, Cash App, check)"], ["stripe", "Stripe (online)"]].map(([k, l]) => (
          <button key={k} onClick={() => setMethod(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${method === k ? "bg-crimson text-parchment" : "bg-parchment text-crimson hover:bg-gold-light"}`} data-testid={`ledger-method-${k}`}>{l}</button>
        ))}
      </div>
      <LedgerFilters f={f} setF={setF} accounts={accounts} pg={pg} data={data} />
      <BulkTagBar sel={sel} setSel={setSel} accounts={accounts} onDone={reload} />
      <div className="mt-3 glass overflow-x-auto rounded-2xl">
        <Pager p={pg} total={pg.filtered.length} noun="payments" testId="ledger-pager" extra={filteredTotal} />
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-crimson text-parchment"><tr>
            <th className="p-3"><Checkbox checked={allSel} onCheckedChange={toggleAll} className="border-parchment" data-testid="ledger-select-all" /></th>
            <th className="p-3 text-left">Date</th><th className="p-3 text-left">Member</th><th className="p-3 text-left">Account</th>
            <th className="p-3 text-left">Method</th><th className="p-3 text-left">Description</th><th className="p-3 text-right">Amount</th>{canVoid && <th className="p-3" />}</tr></thead>
          <tbody>
            {rows.map((p) => <LedgerRow key={p.id} p={p} selected={sel.includes(p.id)} onSelect={toggleOne(p.id)} accounts={accounts} canVoid={canVoid} reload={reload} />)}
            {data && rows.length === 0 && <tr><td className="p-3 text-muted-foreground" colSpan={8}>No payments match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
